<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Counts down to an instant. This is the only block in the layer that uses a
 * client lifecycle hook, and the hydration rule it follows is worth stating:
 *
 *   `remaining` stays null on the server AND through the client's first
 *   (hydration) render, so both produce identical markup — placeholder digits.
 *   The clock is read for the first time in onMounted, after hydration has
 *   finished. Reading Date.now() during setup would mismatch on every load,
 *   because server time and client time are never the same millisecond.
 *
 * The placeholders are fixed-width and tabular, so the real digits arriving a
 * frame later cause no layout shift.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const isExternal = (url?: string | null) => !!url && /^(https?:|mailto:|tel:)/.test(url)

const targetMs = computed(() => {
  const parsed = Date.parse(String(props.data.target_at ?? ''))
  return Number.isNaN(parsed) ? null : parsed
})

const remaining = ref<number | null>(null)
let timer: ReturnType<typeof setInterval> | undefined

const tick = () => {
  if (targetMs.value === null) return
  remaining.value = Math.max(0, targetMs.value - Date.now())
  if (remaining.value === 0 && timer) {
    clearInterval(timer)
    timer = undefined
  }
}

onMounted(() => {
  tick()
  // Already past on load → never start an interval at all.
  if (remaining.value !== 0) timer = setInterval(tick, 1000)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

const expired = computed(() => remaining.value === 0)

const units = computed(() => {
  const ms = remaining.value
  if (ms === null) return null
  const total = Math.floor(ms / 1000)
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor(total / 3600) % 24,
    minutes: Math.floor(total / 60) % 60,
    seconds: total % 60
  }
})

const pad = (n: number) => String(n).padStart(2, '0')

const boxes = computed(() => [
  { key: 'days', label: props.data.labels?.days || 'Days', value: units.value ? String(units.value.days) : '--' },
  { key: 'hours', label: props.data.labels?.hours || 'Hours', value: units.value ? pad(units.value.hours) : '--' },
  { key: 'minutes', label: props.data.labels?.minutes || 'Minutes', value: units.value ? pad(units.value.minutes) : '--' },
  { key: 'seconds', label: props.data.labels?.seconds || 'Seconds', value: units.value ? pad(units.value.seconds) : '--' }
])
</script>

<template>
  <div class="text-center">
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
    />

    <p
      v-if="expired && data.expired_text"
      class="site-display mt-6 text-xl tracking-tight"
    >
      {{ data.expired_text }}
    </p>

    <div
      v-else-if="targetMs !== null"
      class="mt-6 flex flex-wrap justify-center gap-3"
      role="timer"
    >
      <div
        v-for="box in boxes"
        :key="box.key"
        class="site-surface-card min-w-[4.5rem] px-4 py-3"
      >
        <span class="site-display block text-2xl tabular-nums sm:text-3xl">{{ box.value }}</span>
        <span class="mt-1 block text-xs uppercase tracking-wider text-[var(--site-muted)]">{{ box.label }}</span>
      </div>
    </div>

    <div
      v-if="data.button_text"
      class="mt-6"
    >
      <NuxtLink
        :to="data.button_url || '#'"
        :external="isExternal(data.button_url)"
        class="site-btn-accent inline-block px-6 py-3 font-semibold transition"
      >
        {{ data.button_text }}
      </NuxtLink>
    </div>
  </div>
</template>
